import React, { useMemo } from 'react'
import config from 'constants/config'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import { OfferPackageOptionView, arePricesLoading, isBNBLAvailable, getCheapestAvailablePackageOption, getFromDurationLabel } from 'selectors/offerPage/offerPageSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Caption from 'components/Luxkit/Typography/Caption'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import WalledContent from 'components/Common/WalledContent'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import Group from 'components/utils/Group'
import PriceRowValueDiscount from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscount'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import { getActivePackagesByRoom, getMultiRoomPackageTotals, MultiRoomPackageTotals, PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import MobileMultipleRoomsDurationLabel from 'components/OfferPage/MultipleRoomsBooking/Mobile/MobileMultipleRoomsDurationLabel'

interface Props {
  offer: App.Offer;
  rooms: Array<App.Occupants>;
  cheapestOption: OfferPackageOptionView;
  durationLabel: string;
  flightOrigin?: App.Airport;
  airports: Array<App.Airport>;
  showHotelPriceWithFlights: boolean;
  isFlightSelected: boolean;
  isMultiRoom: boolean;
  multiRoomPackageTotals: MultiRoomPackageTotals;
  multiRoomPackages: PackageOptionViewByRoom;
}

function getDiscountPercentage(
  showDiscountPill: boolean,
  isMultiRoom: boolean,
  multipleRoomHighestDiscountPercent: number | undefined,
  isBundledWithFlightsOnly: boolean,
  bundledDiscountPercent: number,
  hotelDiscountPercent: number,
): number | undefined {
  if (!showDiscountPill) return undefined

  if (isMultiRoom) {
    return multipleRoomHighestDiscountPercent
  }
  return isBundledWithFlightsOnly ? bundledDiscountPercent : hotelDiscountPercent
}

function calculateShouldShowValue({
  isMultiRoom,
  hotelValue,
  hotelPrice,
  multiRoomShouldDisplayValue,
  multiRoomHotelValue,
  multiRoomHotelPrice,
  pkgShouldDisplayValue,
}: {
  isMultiRoom: boolean;
  hotelValue: number;
  hotelPrice: number;
  multiRoomShouldDisplayValue: boolean;
  multiRoomHotelValue: number;
  multiRoomHotelPrice: number;
  pkgShouldDisplayValue: boolean;
}): boolean {
  if (isMultiRoom) {
    return multiRoomHotelValue > multiRoomHotelPrice && multiRoomShouldDisplayValue
  }
  return hotelValue > hotelPrice && pkgShouldDisplayValue
}

function calculateOriginalValue({
  isMultiRoom,
  bundledWithFlightsOnly,
  hotelValue,
  flightPrice,
  multiRoomHotelValue,
  multiRoomFlightPrice,
  isFlightSelected,
}: {
  isMultiRoom: boolean;
  bundledWithFlightsOnly: boolean;
  hotelValue: number;
  flightPrice: number;
  multiRoomHotelValue: number;
  multiRoomFlightPrice: number;
  isFlightSelected: boolean;
}): number {
  if (isMultiRoom) {
    if (bundledWithFlightsOnly) {
      return multiRoomHotelValue + multiRoomFlightPrice
    } else {
      return isFlightSelected ? multiRoomHotelValue + multiRoomFlightPrice : multiRoomHotelValue
    }
  } else {
    if (bundledWithFlightsOnly) {
      return hotelValue + flightPrice
    } else {
      return hotelValue
    }
  }
}

function shouldDisplayPill(
  isMultiRoom: boolean,
  isOfferDiscountPillHidden: boolean,
  isBundledWithFlightsOnly: boolean,
  hotelDiscountPercent: number,
  bundledDiscountPercent: number,
  multipleRoomHighestDiscountPercent: number,
) {
  if (isMultiRoom) {
    const shouldShowDiscountPercent = multipleRoomHighestDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
    return shouldShowDiscountPercent && !isOfferDiscountPillHidden
  } else {
    const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
    const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
    return !isOfferDiscountPillHidden && (shouldShowDiscountPercent && !isBundledWithFlightsOnly) || (isBundledWithFlightsOnly && shouldShowBundledDiscountPercent)
  }
}

const defaultMultiRoomTotals:MultiRoomPackageTotals = {
  flightPrice: 0,
  hotelMemberPrice: 0,
  hotelPlusFlightsMemberPrice: 0,
  hotelPlusFlightsPrice: 0,
  hotelPrice: 0,
  hotelValue: 0,
}

function FlashOfferIntroPricingBlock(props: Props) {
  const {
    offer,
    cheapestOption: packageView,
    rooms,
    durationLabel,
    flightOrigin,
    airports,
    showHotelPriceWithFlights,
    isFlightSelected,
    isMultiRoom,
    multiRoomPackageTotals,
    multiRoomPackages,
  } = props
  const {
    flightPrice,
    hotelValue,
    hotelPrice,
    hotelMemberPrice,
    package: pkg,
    hotelDiscountPercent,
    bundledDiscountPercent,
    propertyFees,
    taxesAndFees,
  } = packageView

  const {
    hotelPrice: multiRoomHotelPrice,
    hotelMemberPrice: multiRoomHotelMemberPrice,
    hotelPlusFlightsPrice: multiRoomHotelFlightPrice,
    hotelPlusFlightsMemberPrice: multiRoomMemberFlightPrice,
    flightPrice: multiRoomFlightPrice,
    hotelValue: multiRoomHotelValue,
    shouldDisplayValue: multiRoomShouldDisplayValue,
  } = multiRoomPackageTotals

  const {
    highestDiscountPercent: multipleRoomHighestDiscountPercent,
  } = getHotelOfferDisplayedValue(!!offer.bundledWithFlightsOnly, isFlightSelected ? multiRoomHotelFlightPrice : multiRoomHotelPrice, isFlightSelected ? multiRoomHotelValue + multiRoomFlightPrice : multiRoomHotelValue, !!multiRoomShouldDisplayValue)

  const totalPrice = useMemo(() => {
    let total = hotelPrice + (propertyFees ?? 0)
    if (offer.bundledWithFlightsOnly) {
      total += flightPrice
    }
    return total
  }, [flightPrice, hotelPrice, offer.bundledWithFlightsOnly, propertyFees])

  const totalMemberPrice = useMemo(() => {
    let total = hotelMemberPrice
    if (hotelMemberPrice > 0) {
      total = hotelMemberPrice + (propertyFees ?? 0)
      if (offer.bundledWithFlightsOnly) {
        total += flightPrice
      }
    }
    return total
  }, [flightPrice, hotelMemberPrice, offer.bundledWithFlightsOnly, propertyFees])

  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])
  const showDiscountPill = shouldDisplayPill(isMultiRoom, !!offer?.isDiscountPillHidden, !!offer.bundledWithFlightsOnly, hotelDiscountPercent, bundledDiscountPercent, multipleRoomHighestDiscountPercent)

  const showPriceDetails = taxesAndFees > 0 || (propertyFees ?? 0) > 0 || config.agentHub.isEnabled
  const shouldShowValue = calculateShouldShowValue({
    isMultiRoom,
    hotelValue,
    hotelPrice,
    multiRoomShouldDisplayValue: !!multiRoomShouldDisplayValue,
    multiRoomHotelValue,
    multiRoomHotelPrice,
    pkgShouldDisplayValue: pkg.shouldDisplayValue,
  })

  const originalValue = calculateOriginalValue({
    isMultiRoom,
    bundledWithFlightsOnly: !!offer.bundledWithFlightsOnly,
    hotelValue,
    flightPrice,
    multiRoomHotelValue,
    multiRoomFlightPrice,
    isFlightSelected,
  })

  const saleUnit = useMemo(() => {
    if (offer.bundledWithFlightsOnly) return 'package*'
    return 'total'
  }, [offer.bundledWithFlightsOnly])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      skip: !offer.bundledWithFlightsOnly,
    }),
  ], [offer, packageView])

  const multiRoomLoyaltyHotelOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return Object.values(multiRoomPackages)?.map(packageView => (
      generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView })
    ))
  }, [offer, multiRoomPackages])

  const multiRoomPointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    ...multiRoomLoyaltyHotelOptions,
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      flightPrice: multiRoomFlightPrice,
      skip: !isFlightSelected,
    }),
  ], [offer, isFlightSelected, multiRoomFlightPrice, multiRoomLoyaltyHotelOptions])

  const flightDestination = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])
  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: flightDestination?.code,
  })
  const showFlightsDealTag = flightOrigin && flightDestination && deals.length > 0

  const offerPrice = useMemo(() => {
    const multiRoomPrice = isFlightSelected ? multiRoomHotelFlightPrice : multiRoomHotelPrice
    return isMultiRoom ? multiRoomPrice : totalPrice
  }, [isMultiRoom, totalPrice, isFlightSelected, multiRoomHotelFlightPrice, multiRoomHotelPrice])

  const offerMemberPrice = useMemo(() => {
    const multiRoomMemberPrice = isFlightSelected ? multiRoomMemberFlightPrice : multiRoomHotelMemberPrice
    return isMultiRoom ? multiRoomMemberPrice : totalMemberPrice
  }, [isMultiRoom, multiRoomHotelMemberPrice, isFlightSelected, totalMemberPrice, multiRoomMemberFlightPrice])

  if (showHotelPriceWithFlights && !isMultiRoom) {
    return <WalledContent enforced={offer.walledGarden}>
      <Group direction="vertical" gap={12} horizontalAlign="center">
        <Group direction="vertical" fullWidth gap={12}>
          {showDiscountPill && <PriceRowValueDiscount type="estimate" discountPercentage={hotelDiscountPercent} />}
          <AccommodationPackageTilePricingBlocks>
            <VerticalSpacer gap={12}>
              <BodyTextBlock variant="large" weight="bold">Hotel</BodyTextBlock>
              <VerticalSpacer gap={8}>
                <div>
                  <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    memberPrice={totalMemberPrice}
                    price={totalPrice}
                    size="L"
                    saleUnit={saleUnit}
                    offerConfig={offer.luxPlus}
                    leadWithLuxPlusPrice
                  />
                  {showIncludedGuestsExceeded && (
                    <AdditionalGuestsPopup
                      complex={false}
                      description="+ Extra guest surcharge"
                      modalContent={pkg.roomPolicyDescription}
                    />
                  )}
                  {shouldShowValue && <PriceRowValueDiscountWithCaption
                    data-testid="value-box"
                    size="L"
                    originalValue={hotelValue}
                  />}
                  <PriceRowTaxesAndFees
                    endIcon={showPriceDetails && <OfferPriceDetailsRow
                      offerPage
                      offer={offer}
                      duration={pkg.duration}
                      propertyFees={packageView.propertyFees}
                      taxesAndFees={packageView.taxesAndFees}
                      hotelPrice={packageView.hotelPrice}
                      hotelMemberPrice={packageView.hotelMemberPrice}
                      showMemberPrice={packageView.hotelMemberPrice > 0}
                      flightPrice={packageView.flightPrice}
                      rooms={rooms}
                      extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                      dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                      trigger="info-icon"
                      cta={<TextButton kind="primary" fit="flex">View {config.brandConfig.travelSynonym} options</TextButton>}
                    />}
                  />
                </div>
              </VerticalSpacer>
            </VerticalSpacer>
            <VerticalSpacer gap={12}>
              <BodyTextBlock startIcon={<LinePlaneDepartureIcon />} variant="large" weight="bold">Hotel + flights</BodyTextBlock>
              <VerticalSpacer gap={8}>
                <div>
                  <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    memberPrice={totalMemberPrice > 0 ? totalMemberPrice + flightPrice : 0}
                    price={totalPrice + flightPrice}
                    size="L"
                    saleUnit={saleUnit}
                    offerConfig={offer.luxPlus}
                    leadWithLuxPlusPrice
                  />
                  {showIncludedGuestsExceeded && (
                    <AdditionalGuestsPopup
                      complex={false}
                      description="+ Extra guest surcharge"
                      modalContent={pkg.roomPolicyDescription}
                    />
                  )}
                  {shouldShowValue && <PriceRowValueDiscountWithCaption
                    data-testid="value-box"
                    size="L"
                    originalValue={hotelValue + flightPrice}
                  />}
                  <PriceRowTaxesAndFees
                    endIcon={showPriceDetails && <OfferPriceDetailsRow
                      offerPage
                      offer={offer}
                      duration={pkg.duration}
                      propertyFees={packageView.propertyFees}
                      taxesAndFees={packageView.taxesAndFees}
                      hotelPrice={packageView.hotelPrice}
                      hotelMemberPrice={packageView.hotelMemberPrice}
                      showMemberPrice={packageView.hotelMemberPrice > 0}
                      flightPrice={packageView.flightPrice}
                      rooms={rooms}
                      extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                      dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                      trigger="info-icon"
                      cta={<TextButton kind="primary" fit="flex">View {config.brandConfig.travelSynonym} options</TextButton>}
                    />}
                  />
                </div>
                {showFlightsDealTag && <UrgencyLabel variant="critical1" icon={<SolidFireIcon />}>Flight deal available</UrgencyLabel>}
              </VerticalSpacer>
            </VerticalSpacer>
          </AccommodationPackageTilePricingBlocks>
        </Group>
        <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
      </Group>
    </WalledContent>
  }
  return <WalledContent enforced={offer.walledGarden}>
    <VerticalSpacer gap={8}>
      <div>
        {!isMultiRoom && <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>}
        {isMultiRoom &&
          <PriceRowPriceCaption>
            <MobileMultipleRoomsDurationLabel shouldDisplayRoom />
          </PriceRowPriceCaption>}
        <LuxPlusPriceStack
          memberPrice={offerMemberPrice}
          price={offerPrice}
          size="L"
          saleUnit={saleUnit}
          offerConfig={offer.luxPlus}
          leadWithLuxPlusPrice
        />
        {offer.bundledWithFlightsOnly && !rooms && <Caption variant="medium" colour="neutral-three">
        &nbsp;Two Adults
        </Caption>}
        {showIncludedGuestsExceeded && (
          <AdditionalGuestsPopup
            complex={false}
            description="+ Extra guest surcharge"
            modalContent={pkg.roomPolicyDescription}
          />
        )}
        {shouldShowValue && <PriceRowValueDiscountWithCaption
          data-testid="value-box"
          size="L"
          originalValue={originalValue}
          discountPercentage={getDiscountPercentage(
            !!showDiscountPill,
            isMultiRoom,
            multipleRoomHighestDiscountPercent,
            !!offer.bundledWithFlightsOnly,
            bundledDiscountPercent,
            hotelDiscountPercent,
          )}
        />}
        {!isMultiRoom && <PriceRowTaxesAndFees
          endIcon={showPriceDetails && <OfferPriceDetailsRow
            offerPage
            offer={offer}
            duration={pkg.duration}
            propertyFees={packageView.propertyFees}
            taxesAndFees={packageView.taxesAndFees}
            hotelPrice={packageView.hotelPrice}
            hotelMemberPrice={packageView.hotelMemberPrice}
            showMemberPrice={packageView.hotelMemberPrice > 0}
            flightPrice={offer.bundledWithFlightsOnly ? packageView.flightPrice : undefined}
            rooms={rooms}
            extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
            dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
            trigger="info-icon"
            cta={
              <TextButton
                kind="primary"
                fit="flex"
              >
                View {config.brandConfig.travelSynonym} options
              </TextButton>
          }
          />}
        />}
      </div>
      {!isMultiRoom && <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" /> }
      {isMultiRoom && <LoyaltyPointsEarn calculationRequests={multiRoomPointsEarnCalculationRequests} calculationType="estimate" /> }
    </VerticalSpacer>
  </WalledContent>
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  return {
    cheapestOption: getCheapestAvailablePackageOption(state, appState),
    rooms: state.rooms,
    isFlightSelected: state.flights,
    offer: state.offer,
    flightOrigin: state.airport,
    calendarsLoading: arePricesLoading(appState, state),
    isBNBLAvailable: isBNBLAvailable(state),
    isSpoofed: appState.auth.account.isSpoofed,
    durationLabel: getFromDurationLabel(state, appState),
    airports: appState.geo.airports,
    multiRoomPackages: getActivePackagesByRoom(state, appState),
    isMultiRoom,
    multiRoomPackageTotals: isMultiRoom ? getMultiRoomPackageTotals(state, appState) : defaultMultiRoomTotals,
  }
}, FlashOfferIntroPricingBlock)
